import React, { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAuthenticationService } from '../types/UserAuthenticationService';
import { Auth0Handler } from './Auth0Handler';
import { GleamerAuth0Provider } from './GleamerAuth0Provider';

// Must match EXPIRED_TIME_KEY in gdata-extension/src/utils/IdleTimeout.ts
const EXPIRED_TIME_KEY = '_expiredTime';

type ProviderProps = {
  UserAuthenticationService: UserAuthenticationService;
};

export function Provider({
  children,
  UserAuthenticationService,
}: PropsWithChildren<ProviderProps>) {
  const navigate = useNavigate();
  const onRedirectCallback = (appState) => {
    localStorage.setItem(EXPIRED_TIME_KEY, `${Date.now() + 2 * 60_000}`);
    navigate(appState?.returnTo || window.location.pathname);
  };

  const { auth0 } = window as unknown as Window &
    typeof globalThis & {
      auth0: {
        domain: string;
        clientId: string;
        audience: string;
      };
    };

  return (
    <GleamerAuth0Provider
      cacheLocation="localstorage"
      useRefreshTokens
      domain={auth0.domain}
      clientId={auth0.clientId}
      authorizationParams={{
        // `audience` MUST live in `authorizationParams` for @auth0/auth0-react v2.
        // It used to be spread in via `{...auth0}` at the top level (v1 style),
        // which v2 ignores → the token was minted with an empty audience, so
        // `getAccessTokenSilently` + `useRefreshTokens` failed to renew
        // ("Missing Refresh Token (audience: '')") and dropped the API token.
        audience: auth0.audience,
        redirect_uri: window.location.origin,
      }}
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0Handler UserAuthenticationService={UserAuthenticationService}>
        {children}
      </Auth0Handler>
    </GleamerAuth0Provider>
  );
}
